import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { getApiBase, getAuthHeaders } from '../../utils/api';
import { X, Loader2, Mic, Search, UserPlus, Calendar, AlertCircle } from 'lucide-react';

const HONORIFICS = ['', 'Dr.', 'Prof.', 'Mr.', 'Mrs.', 'Ms.', 'Mx.'];

// Programs in these states are closed — never offered as a "file into existing" target.
const INACTIVE_STATUSES = ['completed', 'cancelled'];

// Split a leading honorific out of a parsed name, e.g. "Dr. Timothy Lynch"
// -> { honorific: 'Dr.', name: 'Timothy Lynch' }. Returns canonical dropdown values.
const splitHonorific = (raw) => {
  const name = (raw || '').trim();
  if (!name) return { honorific: '', name: '' };
  const tokens = name.split(/\s+/);
  const stem = tokens[0].replace(/\.$/, '').toLowerCase();
  const match = HONORIFICS.find((h) => h && h.replace(/\.$/, '').toLowerCase() === stem);
  if (match && tokens.length > 1) {
    return { honorific: match, name: tokens.slice(1).join(' ') };
  }
  return { honorific: '', name };
};

const speakerLabel = (s) =>
  s.full_name || `${s.first_name || ''} ${s.last_name || ''}`.trim() || 'Speaker';

/**
 * Quick "New Speaker Program" wizard for the speaker_bureau paradigm. Launched
 * from inbox items (speaker_meeting, hotel, flight, rental) to set up the
 * speaker + program and file the item in one go.
 *
 * Step 1: pick an existing speaker (typeahead) OR create a new one inline. To
 *   avoid duplicating a speaker that already exists, the parsed name/email is
 *   matched (GET /speakers/match) and any candidate is surfaced as a
 *   "possible existing speaker" the planner confirms.
 * Step 2: create a NEW program, or — when the matched speaker already has an
 *   active engagement — file into one of their existing programs.
 *
 * Submits to POST /speakers/quick-program (find-or-create speaker; create a new
 * trip, or link to an existing trip when program.trip_id is supplied).
 *
 * Props: onClose(), onCreated({ speaker, trip, component }), prefill?,
 *   createMeetingComponent?
 *   prefill = {
 *     speakerName?, speakerEmail?,                  // prefill a NEW speaker
 *     matchedSpeakerId?,                            // confident parse-time match
 *     matchSuggestions?: [{id, full_name, email, affiliation, tier}],
 *     program?: { title, start_date, destination, sponsor, product },
 *   }
 *
 * createMeetingComponent: when true (the header "+ New" / Speakers page "New
 * Program" entry points), the program fields also create a speaker_meeting
 * component on the trip's itinerary — new program or existing one alike.
 * Start/end times are optional; without a start time the meeting is created
 * as a TBD item. The inbox flow leaves this off: there, filing the parsed
 * confirmation is what creates the meeting.
 */
const CreateSpeakerProgramModal = ({ onClose, onCreated, prefill = null, createMeetingComponent = false }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [selectedSpeaker, setSelectedSpeaker] = useState(null);
  const [creatingNew, setCreatingNew] = useState(!!(prefill && prefill.speakerName));
  const [newSpeaker, setNewSpeaker] = useState(() => {
    const { honorific, name } = splitHonorific(prefill && prefill.speakerName);
    return {
      honorific,
      full_name: name,
      email: (prefill && prefill.speakerEmail) || '',
    };
  });

  // Possible-existing-speaker candidates for the parsed name (duplicate guard).
  const [suggestions, setSuggestions] = useState((prefill && prefill.matchSuggestions) || []);

  // Program mode: create a new program, or file into an existing active one.
  const [programMode, setProgramMode] = useState('new');
  const [engagements, setEngagements] = useState([]);
  const [loadingEngagements, setLoadingEngagements] = useState(false);
  const [selectedTripId, setSelectedTripId] = useState('');

  const [program, setProgram] = useState({
    title: (prefill && prefill.program && prefill.program.title) || '',
    start_date: (prefill && prefill.program && prefill.program.start_date) || '',
    start_time: (prefill && prefill.program && prefill.program.start_time) || '',
    end_time: (prefill && prefill.program && prefill.program.end_time) || '',
    destination: (prefill && prefill.program && prefill.program.destination) || '',
    sponsor: (prefill && prefill.program && prefill.program.sponsor) || '',
    product: (prefill && prefill.program && prefill.program.product) || '',
  });

  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const debounceRef = useRef(null);

  // On open: if parse-time found a confident match, preselect that speaker so
  // the planner just confirms it (never silently linked downstream).
  useEffect(() => {
    const matchedId = prefill && prefill.matchedSpeakerId;
    if (!matchedId) return;
    (async () => {
      try {
        const res = await axios.get(`${getApiBase()}/speakers/${matchedId}`, { headers: getAuthHeaders() });
        if (res.data) {
          setSelectedSpeaker(res.data);
          setCreatingNew(false);
        }
      } catch {
        /* fall back to the search/new flow */
      }
    })();
  }, []);

  // Typeahead search for existing speakers.
  useEffect(() => {
    if (selectedSpeaker || creatingNew) return;
    if (!query.trim()) {
      setResults([]);
      return;
    }
    setSearching(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await axios.get(`${getApiBase()}/speakers`, {
          headers: getAuthHeaders(),
          params: { q: query.trim(), limit: 8 },
        });
        setResults(res.data || []);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [query, selectedSpeaker, creatingNew]);

  // Duplicate guard: while entering a new speaker, look for existing matches.
  useEffect(() => {
    if (selectedSpeaker || !creatingNew) return;
    const name = newSpeaker.full_name.trim();
    const email = newSpeaker.email.trim();
    if (!name && !email) {
      setSuggestions([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(`${getApiBase()}/speakers/match`, {
          headers: getAuthHeaders(),
          params: { name, email, limit: 5 },
        });
        setSuggestions(res.data || []);
      } catch {
        /* keep any prefill suggestions */
      }
    }, 300);
    return () => clearTimeout(t);
  }, [newSpeaker.full_name, newSpeaker.email, creatingNew, selectedSpeaker]);

  // When a speaker is selected, load their ACTIVE programs so the planner can
  // file into an existing engagement instead of always creating a new one.
  useEffect(() => {
    if (!selectedSpeaker) {
      setEngagements([]);
      setProgramMode('new');
      setSelectedTripId('');
      return;
    }
    setLoadingEngagements(true);
    (async () => {
      try {
        const res = await axios.get(`${getApiBase()}/speakers/${selectedSpeaker.id}/engagements`, {
          headers: getAuthHeaders(),
        });
        const all = [...(res.data?.upcoming || []), ...(res.data?.past || [])];
        const active = all.filter((t) => !INACTIVE_STATUSES.includes((t.status || '').toLowerCase()));
        setEngagements(active);
      } catch {
        setEngagements([]);
      } finally {
        setLoadingEngagements(false);
      }
    })();
  }, [selectedSpeaker]);

  const useSuggestion = (candidate) => {
    setSelectedSpeaker(candidate);
    setSuggestions([]);
    setCreatingNew(false);
    setResults([]);
    setQuery('');
  };

  const speakerReady = selectedSpeaker || (creatingNew && newSpeaker.full_name.trim());
  const filingExisting = programMode === 'existing';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!speakerReady) {
      setError('Please select or enter a speaker.');
      return;
    }
    if (filingExisting) {
      if (!selectedTripId) {
        setError('Please choose an existing program.');
        return;
      }
      if (createMeetingComponent && !program.title.trim()) {
        setError('Please enter a meeting title / topic.');
        return;
      }
    } else if (!program.title.trim()) {
      setError('Please enter a program title.');
      return;
    }
    setSaving(true);
    try {
      const body = {};
      if (selectedSpeaker) {
        body.speaker_id = selectedSpeaker.id;
      } else {
        body.speaker = { ...newSpeaker };
      }
      if (filingExisting) {
        body.trip_id = selectedTripId;
        // The program fields describe the meeting being added to that program
        if (createMeetingComponent) body.program = { ...program };
      } else {
        body.program = { ...program };
      }
      if (createMeetingComponent) body.create_meeting_component = true;
      const res = await axios.post(`${getApiBase()}/speakers/quick-program`, body, { headers: getAuthHeaders() });
      onCreated && onCreated(res.data);
      onClose && onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create program.');
    } finally {
      setSaving(false);
    }
  };

  const submitDisabled = saving || !speakerReady
    || (filingExisting
      ? (!selectedTripId || (createMeetingComponent && !program.title.trim()))
      : !program.title.trim());

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ui-overlay/50 p-4" onClick={onClose}>
      <div
        className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl bg-ui-surface shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-ui-line px-6 py-4">
          <div className="flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-ui-red-strong text-ui-on-primary">
              <Mic className="h-5 w-5" />
            </div>
            <h2 className="text-lg font-semibold text-ui-ink">New Speaker Program</h2>
          </div>
          <button onClick={onClose} className="rounded-lg p-1 text-ui-muted hover:bg-ui-hover">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5 px-6 py-5">
          {error && <div className="rounded-lg bg-ui-red-bg px-3 py-2 text-sm text-ui-red-fg">{error}</div>}

          {/* Step 1: speaker */}
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-ui-muted">
              Speaker
            </label>

            {selectedSpeaker ? (
              <div className="flex items-center justify-between rounded-lg border border-ui-red-line bg-ui-red-bg px-3 py-2">
                <span className="text-sm font-medium text-ui-ink">
                  {speakerLabel(selectedSpeaker)}
                  {selectedSpeaker.email ? <span className="ml-2 text-ui-muted">{selectedSpeaker.email}</span> : null}
                </span>
                <button type="button" onClick={() => setSelectedSpeaker(null)} className="text-xs font-medium text-ui-red-fg">
                  Change
                </button>
              </div>
            ) : creatingNew ? (
              <div className="space-y-3 rounded-lg border border-ui-line p-3">
                {/* Duplicate guard: surface possible existing speakers to confirm */}
                {suggestions.length > 0 && (
                  <div className="rounded-lg border border-ui-amber-line bg-ui-amber-bg p-2.5">
                    <div className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold text-ui-amber-fg">
                      <AlertCircle className="h-3.5 w-3.5" />
                      Possible existing speaker — use them instead of creating a duplicate?
                    </div>
                    <div className="space-y-1">
                      {suggestions.map((s) => (
                        <button
                          type="button"
                          key={s.id}
                          onClick={() => useSuggestion(s)}
                          className="flex w-full items-center justify-between rounded-md bg-ui-surface px-2.5 py-1.5 text-left text-sm hover:bg-ui-amber-bg"
                        >
                          <span className="font-medium text-ui-ink">
                            {speakerLabel(s)}
                            {s.affiliation ? <span className="ml-2 text-xs text-ui-muted">{s.affiliation}</span> : null}
                          </span>
                          <span className="ml-2 text-xs font-medium text-ui-amber-fg">Use this speaker</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <p className="text-xs font-medium text-ui-muted">Confirm the speaker — edit name, title, or email before creating.</p>
                <div className="flex gap-2">
                  <select
                    value={newSpeaker.honorific}
                    onChange={(e) => setNewSpeaker((p) => ({ ...p, honorific: e.target.value }))}
                    className="h-10 w-24 rounded-lg border border-ui-line px-2 text-sm"
                    title="Title"
                  >
                    {HONORIFICS.map((h) => (
                      <option key={h} value={h}>{h || 'Title'}</option>
                    ))}
                  </select>
                  <input
                    placeholder="Speaker name (e.g. Timothy Lynch)"
                    value={newSpeaker.full_name}
                    onChange={(e) => setNewSpeaker((p) => ({ ...p, full_name: e.target.value }))}
                    className="h-10 flex-1 rounded-lg border border-ui-line px-3 text-sm"
                  />
                </div>
                <input
                  placeholder="Email (optional)"
                  value={newSpeaker.email}
                  onChange={(e) => setNewSpeaker((p) => ({ ...p, email: e.target.value }))}
                  className="h-10 w-full rounded-lg border border-ui-line px-3 text-sm"
                />
                <button type="button" onClick={() => setCreatingNew(false)} className="text-xs font-medium text-ui-muted">
                  ← Search existing speakers
                </button>
              </div>
            ) : (
              <div>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ui-muted" />
                  <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search speakers by name or email..."
                    className="h-10 w-full rounded-lg border border-ui-line pl-9 pr-3 text-sm"
                  />
                  {searching && <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-ui-muted" />}
                </div>
                {results.length > 0 && (
                  <div className="mt-1 max-h-44 overflow-y-auto rounded-lg border border-ui-line">
                    {results.map((s) => (
                      <button
                        type="button"
                        key={s.id}
                        onClick={() => { setSelectedSpeaker(s); setResults([]); setQuery(''); }}
                        className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-ui-red-bg"
                      >
                        <span className="font-medium text-ui-ink">{speakerLabel(s)}</span>
                        {s.email && <span className="text-xs text-ui-muted">{s.email}</span>}
                      </button>
                    ))}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => { setCreatingNew(true); setResults([]); }}
                  className="mt-2 flex items-center gap-1 text-sm font-medium text-ui-red-fg"
                >
                  <UserPlus className="h-4 w-4" /> New speaker
                </button>
              </div>
            )}
          </div>

          {/* Step 2: program — new, or file into an existing active program */}
          <div className="space-y-3 border-t border-ui-divider pt-4">
            <div className="flex items-center justify-between">
              <label className="block text-xs font-semibold uppercase tracking-wide text-ui-muted">Program</label>
              {/* Existing-program option only when a known speaker has active programs */}
              {selectedSpeaker && engagements.length > 0 && (
                <div className="flex rounded-lg border border-ui-line p-0.5 text-xs">
                  <button
                    type="button"
                    onClick={() => setProgramMode('new')}
                    className={`rounded-md px-2.5 py-1 font-medium ${programMode === 'new' ? 'bg-ui-red-strong text-ui-on-primary' : 'text-ui-ink-3'}`}
                  >
                    New program
                  </button>
                  <button
                    type="button"
                    onClick={() => setProgramMode('existing')}
                    className={`rounded-md px-2.5 py-1 font-medium ${programMode === 'existing' ? 'bg-ui-red-strong text-ui-on-primary' : 'text-ui-ink-3'}`}
                  >
                    Existing program
                  </button>
                </div>
              )}
            </div>

            {loadingEngagements && selectedSpeaker && (
              <div className="flex items-center gap-2 text-xs text-ui-muted">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking existing programs…
              </div>
            )}

            {filingExisting && (
              <>
                <select
                  value={selectedTripId}
                  onChange={(e) => setSelectedTripId(e.target.value)}
                  className="h-10 w-full rounded-lg border border-ui-line px-3 text-sm"
                >
                  <option value="">Choose an active program…</option>
                  {engagements.map((t) => (
                    <option key={t.id} value={t.id}>
                      {(t.title || 'Untitled Program') + (t.start_date ? ` — ${String(t.start_date).slice(0, 10)}` : '')}
                    </option>
                  ))}
                </select>
                {createMeetingComponent && (
                  <p className="text-xs font-medium text-ui-muted">
                    Meeting details — added to the selected program's itinerary:
                  </p>
                )}
              </>
            )}

            {(!filingExisting || createMeetingComponent) && (
              <>
                <input
                  placeholder={createMeetingComponent ? 'Meeting title / topic *' : 'Program title *'}
                  value={program.title}
                  onChange={(e) => setProgram((p) => ({ ...p, title: e.target.value }))}
                  className="h-10 w-full rounded-lg border border-ui-line px-3 text-sm"
                />
                <div className="grid grid-cols-2 gap-3">
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ui-muted" />
                    <input
                      type="date"
                      value={program.start_date}
                      onChange={(e) => setProgram((p) => ({ ...p, start_date: e.target.value }))}
                      className="h-10 w-full rounded-lg border border-ui-line pl-9 pr-3 text-sm"
                    />
                  </div>
                  <input
                    placeholder="Location"
                    value={program.destination}
                    onChange={(e) => setProgram((p) => ({ ...p, destination: e.target.value }))}
                    className="h-10 w-full rounded-lg border border-ui-line px-3 text-sm"
                  />
                </div>
                {createMeetingComponent && (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="mb-1 block text-xs text-ui-muted">Start time (optional)</label>
                        <input
                          type="time"
                          value={program.start_time}
                          onChange={(e) => setProgram((p) => ({ ...p, start_time: e.target.value }))}
                          className="h-10 w-full rounded-lg border border-ui-line px-3 text-sm"
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-xs text-ui-muted">End time (optional)</label>
                        <input
                          type="time"
                          value={program.end_time}
                          onChange={(e) => setProgram((p) => ({ ...p, end_time: e.target.value }))}
                          className="h-10 w-full rounded-lg border border-ui-line px-3 text-sm"
                        />
                      </div>
                    </div>
                    <p className="text-xs text-ui-muted">
                      No start time yet? The meeting is added to the itinerary as TBD.
                    </p>
                  </>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <input
                    placeholder="Sponsor"
                    value={program.sponsor}
                    onChange={(e) => setProgram((p) => ({ ...p, sponsor: e.target.value }))}
                    className="h-10 w-full rounded-lg border border-ui-line px-3 text-sm"
                  />
                  <input
                    placeholder="Product / brand"
                    value={program.product}
                    onChange={(e) => setProgram((p) => ({ ...p, product: e.target.value }))}
                    className="h-10 w-full rounded-lg border border-ui-line px-3 text-sm"
                  />
                </div>
              </>
            )}
          </div>

          <div className="flex justify-end gap-2 border-t border-ui-divider pt-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-ui-control px-4 py-2 text-sm font-medium text-ui-ink-2 hover:bg-ui-hover"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitDisabled}
              className="flex items-center gap-2 rounded-lg bg-ui-red-strong px-5 py-2 text-sm font-semibold text-ui-on-primary hover:bg-ui-red-strong/90 disabled:opacity-50"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {filingExisting
                ? (createMeetingComponent ? 'Add Meeting to Program' : 'Add to Program')
                : 'Create Program'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateSpeakerProgramModal;
